/**
 * SOLOMON — frontend model registry.
 *
 * Resolves a model ID to its badge presentation by FAMILY, never by exact ID.
 *
 * WHY FAMILY-BASED: three separate hardcoded ID→label maps used to live in
 * ChatInterface/modelBadgeConfig.js, ConversationSidebar/index.jsx and
 * ActionCard/ActionCard.jsx. Each pinned literal model IDs, and all three
 * drifted through the 2026-06-25 and 2026-07-17 backend bumps — by 2026-08 they
 * still listed gpt-4o / claude-opus-4-6 / gemini-1.5-2.x while the backend ran
 * gpt-5.6-sol / claude-opus-4-8 / gemini-3.5-flash. Two were rescued by their
 * partial-match fallbacks; ActionCard's fallback returned the literal string
 * 'GPT-4o' and so mislabelled every GPT-5.x action card in chat.
 *
 * The frontend cannot import backend/src/config/models.js at RUNTIME (separate
 * build), so this is the frontend mirror — but it mirrors the FAMILIES, not the
 * IDs. Model IDs bump every few months; 'opus' / 'gpt' / 'gemini' do not, so a
 * version bump within a known family needs no edit here.
 *
 * It is NOT true that no bump can ever need an edit: an ID carrying no family
 * token would resolve to null (OpenAI's o-series — 'o3', 'o4-mini' — contains no
 * 'gpt'). That is why modelRegistry.test.js IMPORTS the backend registry at test
 * time and asserts every exported chat slot matches a family. If that gate goes
 * red, add a FAMILY, never an ID — an ID list is what rotted last time.
 *
 * Badge colors are fixed by .claude/rules/design-system.md:
 *   Claude → --text-orange · GPT → --status-success · Gemini → --status-info
 *
 * Logos: simple-icons (CC0) SVG paths on the FAMILY descriptor — siClaude /
 * siGooglegemini; GPT has none (the set ships no OpenAI mark) and renders its
 * glyph.
 */

import { siClaude, siGooglegemini } from 'simple-icons';

const CLAUDE = { providerLabel: 'Claude', variant: 'claude', color: 'var(--text-orange)',     provider: 'anthropic', logo: siClaude.path };
// No OpenAI/ChatGPT mark exists in simple-icons 16.31.0 (only 'OpenAI Gym'); GPT keeps its glyph (ruling 2026-09-14).
const GPT    = { providerLabel: 'GPT',    variant: 'gpt',    color: 'var(--status-success)', provider: 'openai',    logo: null };
const GEMINI = { providerLabel: 'Gemini', variant: 'gemini', color: 'var(--status-info)',    provider: 'google',    logo: siGooglegemini.path };

/**
 * Ordered — FIRST MATCH WINS. Narrower tiers (sub-models, mini/lite variants)
 * MUST precede their generic provider entry.
 *
 * `label` is the sub-model name for badges that want the precise brain
 * (◆ Opus / ◇ Sonnet / ◈ Haiku); `providerLabel` is the coarse name for
 * surfaces that dedupe by provider (the conversation sidebar, so a thread
 * using Opus + Sonnet shows one "Claude" chip, not two).
 *
 * 'error' is deliberately NOT a family — it is a render state, not a provider.
 * Consumers that need it use ERROR_DESCRIPTOR explicitly.
 */
const FAMILIES = [
  { pattern: /opus/i,      label: 'Opus',     icon: '◆', ...CLAUDE },
  { pattern: /sonnet/i,    label: 'Sonnet',   icon: '◇', ...CLAUDE },
  { pattern: /haiku/i,     label: 'Haiku',    icon: '◈', ...CLAUDE },
  { pattern: /claude/i,    label: 'Claude',   icon: '◆', ...CLAUDE },
  // '-mini' with the hyphen, not bare 'mini': 'gemini' contains the substring
  // 'mini', so an unanchored /gpt.*mini/ would claim any 'gpt'-then-'gemini' string.
  { pattern: /gpt.*-mini/i, label: 'GPT-mini', icon: '⬡', ...GPT },
  { pattern: /gpt/i,       label: 'GPT',      icon: '⬡', ...GPT },
  { pattern: /gemini/i,    label: 'Gemini',   icon: '✦', ...GEMINI },
];

export const ERROR_DESCRIPTOR = Object.freeze({
  label: 'Error',
  providerLabel: 'Error',
  icon: '✕',
  variant: 'error',
  color: 'var(--status-error)',
});

/**
 * The ask-and-wait turn (routing V3 rev 3.2). `model_used = 'system'` means
 * Solomon asked its clarifying question with no provider behind it — so the
 * badge names Solomon and shows no provider logo. Like 'error', this is a
 * render state, NOT a family: a FAMILIES entry would let /system/i claim any
 * future model ID containing the substring.
 */
export const SYSTEM_DESCRIPTOR = Object.freeze({
  label: 'Solomon',
  providerLabel: 'Solomon',
  icon: '☉',
  variant: 'system',
  color: 'var(--text-orange)',
});

/**
 * @param {string} model — a raw provider model ID (e.g. 'gpt-5.6-sol')
 * @returns {{label:string, providerLabel:string, icon:string, variant:string, color:string}|null}
 *          null for a falsy, non-string, or unrecognised ID. Callers decide
 *          what an unrecognised model renders as — this layer does not guess.
 */
export function resolveModel(model) {
  if (typeof model !== 'string' || !model) return null;
  const family = FAMILIES.find((f) => f.pattern.test(model));
  if (!family) return null;
  const { pattern: _pattern, ...descriptor } = family;
  return descriptor;
}

/** Sub-model label ('Opus', 'GPT-mini'), or null if unrecognised. */
export function getModelLabel(model) {
  return resolveModel(model)?.label ?? null;
}

/** Coarse provider label ('Claude', 'GPT'), or null if unrecognised. */
export function getProviderLabel(model) {
  return resolveModel(model)?.providerLabel ?? null;
}

/**
 * Human name for a raw model id — derived, never mapped (an ID map is what
 * rotted last time). Strip a trailing -YYYYMMDD snapshot, split on '-', join
 * adjacent numeric segments with '.', title-case words, keep GPT upper and
 * re-attach its version with a hyphen. Unknown ids still format; callers that
 * want "raw when unrecognised" check resolveModel first (getModelConfig does).
 *
 *   claude-opus-5-5           → Claude Opus 5.5
 *   claude-haiku-4-5-20251001 → Claude Haiku 4.5
 *   gpt-6.1-sol               → GPT-6.1 Sol
 *   gemini-3.7-flash          → Gemini 3.7 Flash
 */
export function formatModelName(id) {
  if (typeof id !== 'string' || !id) return '';
  const parts = id.replace(/-\d{8}$/, '').split('-').filter(Boolean);
  const out = [];
  for (const part of parts) {
    const isNum = /^\d+(\.\d+)*$/.test(part);
    const prevIsNum = out.length > 0 && /^\d+(\.\d+)*$/.test(out[out.length - 1]);
    if (isNum && prevIsNum) out[out.length - 1] += `.${part}`;
    else if (isNum) out.push(part);
    else if (part.toLowerCase() === 'gpt') out.push('GPT');
    else out.push(part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
  }
  return out.join(' ').replace(/^GPT (\d)/, 'GPT-$1');
}
